import { useEffect, useRef } from 'react';
import { getCurrentToken } from '../../Access/services/authService';
import { getBuildingByLotId } from '../../services/buildingsService';
import { isMapAlive } from '../utils/canAccessMapStyle';
import { BUILDINGS_LAYER_STYLE } from '../styles/mapTheme';

const SOURCE_ID = 'buildings';
const LAYER_FILL_ID = 'buildings-fill';
const LAYER_OUTLINE_ID = 'buildings-outline';
const LAYER_3D_ID = 'buildings-3d';

// Fetch global por bbox + listener moveend desactivados (ver comentarios al final del archivo).
const {
  fillColor,
  fillOpacity,
  outlineColor,
  outlineWidth,
  extrusionColor,
  extrusionOpacity,
  extrusionSelectedColor,
  extrusionSelectedOpacity,
  extrusionDefaultHeight,
  extrusionMinZoom,
} = BUILDINGS_LAYER_STYLE;

const EMPTY_COLLECTION = { type: 'FeatureCollection', features: [] };

/**
 * Capa de edificios 3D (footprint + extrusión).
 *
 * Activo hoy: solo `filterLotId` + GET /getbuilding?lot_id=…
 *
 * Modo global `mode === 'all'` con GET /getbuildingsbybounds en cada moveend.
 *
 * @param {object}                 options
 * @param {React.MutableRefObject} options.mapInstanceRef
 * @param {boolean}                [options.enabled=true]
 * @param {'all'|'selected'}       [options.mode='all'] — solo aplica sub-rama `selected` si la usás
 * @param {object|null}            [options.selectedLotGeometry]
 * @param {string|null}            [options.filterLotId] — dispara /getbuilding y pinta selección
 * @param {'full'|'footprints'|'hidden'} [options.buildingView='full'] — full: 3D+planta; footprints: solo fill+line; hidden: sin capa (usar con enabled=false)
 * @param {function}                [options.onBuildingStatusChange] — cb(status: 'idle'|'loading'|'available'|'unavailable'|'login_required')
 */
export function useBuildingsLayer({
  mapInstanceRef,
  enabled = true,
  mode = 'all',
  selectedLotGeometry = null,
  filterLotId = null,
  buildingView = 'full',
  onBuildingStatusChange,
  selectedExtrusionColor = null,
  selectedExtrusionOpacity = null,
}) {
  const moveEndHandlerRef = useRef(null);
  const mountedRef = useRef(true);
  /** Se incrementa en cada nuevo `filterLotId` para ignorar respuestas obsoletas de /getbuilding */
  const lotFetchGenRef = useRef(0);
  const buildingViewRef = useRef(buildingView);
  buildingViewRef.current = buildingView;
  const onStatusRef = useRef(onBuildingStatusChange);
  onStatusRef.current = onBuildingStatusChange;
  /** Caché del último GeoJSON cargado. Se descarta solo al cambiar de lote (nuevo filterLotId). */
  const buildingCacheRef = useRef({ lotId: null, geojson: null });

  useEffect(() => {
    mountedRef.current = true;

    const map = mapInstanceRef.current;
    if (!map) return undefined;

    const setupAndBind = () => {
      if (!mountedRef.current) return;
      ensureSourceAndLayers(map);
    };

    if (map.isStyleLoaded()) {
      setupAndBind();
    } else {
      map.once('load', setupAndBind);
    }

    return () => {
      mountedRef.current = false;
      unbindMoveEnd(map, moveEndHandlerRef);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapInstanceRef]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    applyVisibility(map, enabled);

    if (!enabled || !filterLotId) {
      // Sin lote (o modo oculto) no puede quedar la extrusión del lote anterior.
      // Un fetch en vuelo tampoco: si llega después, MapLibre la volvería a pintar.
      unbindMoveEnd(map, moveEndHandlerRef);
      lotFetchGenRef.current += 1;
      buildingCacheRef.current = { lotId: null, geojson: null };
      if (map.getSource(SOURCE_ID)) setSourceData(map, EMPTY_COLLECTION);
      onStatusRef.current?.('idle');
      return;
    }

    if (!map.getSource(SOURCE_ID)) return;

    if (filterLotId) {
      unbindMoveEnd(map, moveEndHandlerRef);
      applyExtrusionColor(
        map,
        selectedExtrusionColor || extrusionSelectedColor,
        selectedExtrusionOpacity ?? extrusionSelectedOpacity,
      );

      if (!getCurrentToken()) {
        buildingCacheRef.current = { lotId: filterLotId, geojson: null };
        setSourceData(map, EMPTY_COLLECTION);
        onStatusRef.current?.('login_required');
        return;
      }

      const cache = buildingCacheRef.current;

      if (cache.lotId === filterLotId && cache.geojson !== null) {
        // Datos ya disponibles: restaurar sin nueva petición
        setSourceData(map, cache.geojson);
        if ((cache.geojson.features?.length ?? 0) > 0) {
          if (map.getLayer(LAYER_3D_ID)) applyBuildingViewSublayers(map, buildingViewRef.current);
          onStatusRef.current?.('available');
        } else {
          onStatusRef.current?.('unavailable');
        }
      } else {
        // Nuevo lote o sin caché: vaciar el source y lanzar fetch
        buildingCacheRef.current = { lotId: filterLotId, geojson: null };
        setSourceData(map, EMPTY_COLLECTION);
        onStatusRef.current?.('loading');
        const gen = (lotFetchGenRef.current += 1);
        fetchBuildingByLot(filterLotId, gen, map, mapInstanceRef, mountedRef, lotFetchGenRef, buildingViewRef, onStatusRef, buildingCacheRef);
      }
    }
    // Modo global por bbox — ver ENABLE_GLOBAL_BUILDINGS_BY_BOUNDS + bindMoveEnd/fetchAndRender comentados abajo
  }, [mapInstanceRef, enabled, mode, filterLotId, selectedExtrusionColor, selectedExtrusionOpacity]);

  // Solo planta vs 3D: mismo GeoJSON, se alterna visibilidad de fill-extrusion
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !enabled || buildingView === 'hidden' || !filterLotId) return;
    if (!map.getLayer(LAYER_3D_ID)) return;
    applyBuildingViewSublayers(map, buildingView);
  }, [mapInstanceRef, enabled, filterLotId, buildingView]);

  useEffect(() => {
    if (mode !== 'selected') return;
    const map = mapInstanceRef.current;
    if (!map || !map.isStyleLoaded() || !map.getSource(SOURCE_ID)) return;

    renderSelected(map, selectedLotGeometry);
  }, [mapInstanceRef, mode, selectedLotGeometry]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    return () => {
      mountedRef.current = false;
      if (!map) return;
      unbindMoveEnd(map, moveEndHandlerRef);
      removeLayers(map);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

function ensureSourceAndLayers(map) {
  if (map.getSource(SOURCE_ID)) return;

  map.addSource(SOURCE_ID, { type: 'geojson', data: EMPTY_COLLECTION });

  map.addLayer({
    id: LAYER_FILL_ID,
    type: 'fill',
    source: SOURCE_ID,
    paint: { 'fill-color': fillColor, 'fill-opacity': fillOpacity },
  });

  map.addLayer({
    id: LAYER_OUTLINE_ID,
    type: 'line',
    source: SOURCE_ID,
    paint: { 'line-color': outlineColor, 'line-width': outlineWidth },
  });

  map.addLayer(extrusionLayerDef('visible', extrusionColor, extrusionOpacity));
}

/* Reactivar con ENABLE_GLOBAL_BUILDINGS_BY_BOUNDS
const MIN_ZOOM_FETCH = 14;

function bindMoveEnd(map, mapInstanceRef, mountedRef, moveEndHandlerRef) {
  unbindMoveEnd(map, moveEndHandlerRef);
  const handler = async () => {
    if (!mountedRef.current) return;
    if (map.getZoom() < MIN_ZOOM_FETCH) return;
    await fetchAndRender(map, mapInstanceRef, mountedRef);
  };
  moveEndHandlerRef.current = handler;
  map.on('moveend', handler);
}
*/

function unbindMoveEnd(map, moveEndHandlerRef) {
  if (moveEndHandlerRef.current) {
    map.off('moveend', moveEndHandlerRef.current);
    moveEndHandlerRef.current = null;
  }
}

/* Reactivar con getBuildingsByBounds
async function fetchAndRender(map, mapInstanceRef, mountedRef) {
  if (!mountedRef.current || map !== mapInstanceRef.current) return;
  try {
    const geojson = await getBuildingsByBounds(map.getBounds());
    if (!mountedRef.current || map !== mapInstanceRef.current) return;
    setSourceData(map, geojson);
    console.log(`Buildings loaded: ${geojson?.features?.length ?? 0}`);
  } catch (err) {
    console.warn('useBuildingsLayer (bounds):', err.message);
  }
}
*/

async function fetchBuildingByLot(lotId, fetchGen, map, mapInstanceRef, mountedRef, lotFetchGenRef, buildingViewRef, onStatusRef, buildingCacheRef) {
  if (!mountedRef.current || map !== mapInstanceRef.current || !isMapAlive(map)) return;
  try {
    const geojson = await getBuildingByLotId(lotId);
    if (!mountedRef.current || map !== mapInstanceRef.current || !isMapAlive(map)) return;
    if (lotFetchGenRef.current !== fetchGen) return;
    // Guardar en caché antes de renderizar
    buildingCacheRef.current = { lotId, geojson };
    const hasFeatures = (geojson?.features?.length ?? 0) > 0;
    if (hasFeatures) {
      setSourceData(map, geojson);
      if (map.getLayer(LAYER_3D_ID)) {
        applyBuildingViewSublayers(map, buildingViewRef.current);
      }
      onStatusRef.current?.('available');
    } else {
      setSourceData(map, EMPTY_COLLECTION);
      onStatusRef.current?.('unavailable');
    }
  } catch (err) {
    console.warn('useBuildingsLayer (lot):', err.message);
    onStatusRef.current?.('unavailable');
  }
}

/** `full`: fill + line + extrusion; `footprints`: solo fill + line (oculta extrusión) */
function applyBuildingViewSublayers(map, view) {
  const show3d = view === 'full';
  try {
    if (map.getLayer(LAYER_FILL_ID)) {
      map.setLayoutProperty(LAYER_FILL_ID, 'visibility', 'visible');
    }
    if (map.getLayer(LAYER_OUTLINE_ID)) {
      map.setLayoutProperty(LAYER_OUTLINE_ID, 'visibility', 'visible');
    }
    if (map.getLayer(LAYER_3D_ID)) {
      map.setLayoutProperty(LAYER_3D_ID, 'visibility', show3d ? 'visible' : 'none');
    }
  } catch (_) {}
}

function renderSelected(map, geometry) {
  if (!geometry) {
    setSourceData(map, EMPTY_COLLECTION);
    return;
  }
  setSourceData(map, {
    type: 'FeatureCollection',
    features: [{ type: 'Feature', geometry, properties: {} }],
  });
}

function extrusionLayerDef(visibility, color, opacity) {
  return {
    id: LAYER_3D_ID,
    type: 'fill-extrusion',
    source: SOURCE_ID,
    minzoom: extrusionMinZoom,
    layout: { visibility },
    paint: {
      'fill-extrusion-color': color,
      'fill-extrusion-height': ['coalesce', ['get', 'height'], extrusionDefaultHeight],
      'fill-extrusion-base': 0,
      'fill-extrusion-opacity': opacity,
    },
  };
}

/**
 * MapLibre 3.6 no saca la extrusión anterior con `setData`: el sólido queda
 * en el lote viejo hasta un pan o un zoom (el fill 2D sí se actualiza).
 * Recrear la capa obliga a armar la malla de nuevo.
 */
function refreshExtrusionLayer(map) {
  try {
    if (!isMapAlive(map) || !map.getLayer(LAYER_3D_ID)) return;
    let visibility = 'visible';
    let color = extrusionColor;
    let opacity = extrusionOpacity;
    visibility = map.getLayoutProperty(LAYER_3D_ID, 'visibility') || 'visible';
    color = map.getPaintProperty(LAYER_3D_ID, 'fill-extrusion-color') || extrusionColor;
    const paintOpacity = map.getPaintProperty(LAYER_3D_ID, 'fill-extrusion-opacity');
    if (paintOpacity != null) opacity = paintOpacity;
    const layers = map.getStyle()?.layers || [];
    const index = layers.findIndex((layer) => layer.id === LAYER_3D_ID);
    const beforeId = index >= 0 && index < layers.length - 1 ? layers[index + 1].id : undefined;
    map.removeLayer(LAYER_3D_ID);
    map.addLayer(extrusionLayerDef(visibility, color, opacity), beforeId);
  } catch (_) {
    // Si el remove alcanzó a correr y el add no, la capa no puede quedar ausente.
    try {
      if (isMapAlive(map) && map.getSource(SOURCE_ID) && !map.getLayer(LAYER_3D_ID)) {
        map.addLayer(extrusionLayerDef('visible', extrusionColor, extrusionOpacity));
      }
    } catch (_) { /* estilo recreándose */ }
  }
}

function setSourceData(map, data) {
  if (!isMapAlive(map)) return;
  const src = map.getSource(SOURCE_ID);
  if (src && typeof src.setData === 'function') {
    src.setData(data);
  }
  refreshExtrusionLayer(map);
}

function applyVisibility(map, show) {
  const v = show ? 'visible' : 'none';
  for (const layerId of [LAYER_FILL_ID, LAYER_OUTLINE_ID, LAYER_3D_ID]) {
    try {
      if (map.getLayer(layerId)) {
        map.setLayoutProperty(layerId, 'visibility', v);
      }
    } catch (_) {}
  }
}

function applyExtrusionColor(map, color, opacity) {
  try {
    if (map.getLayer(LAYER_3D_ID)) {
      map.setPaintProperty(LAYER_3D_ID, 'fill-extrusion-color', color);
      map.setPaintProperty(LAYER_3D_ID, 'fill-extrusion-opacity', opacity);
    }
  } catch (_) {}
}

function removeLayers(map) {
  try {
    for (const layerId of [LAYER_3D_ID, LAYER_OUTLINE_ID, LAYER_FILL_ID]) {
      if (map.getLayer(layerId)) map.removeLayer(layerId);
    }
    if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID);
  } catch (_) {}
}
